import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher, customFetcherRaw } from './customFetcher';
import {
    EMPTY_INFO_RESPONSE,
    InfoRoleKey,
    InfoCreateBankAccountInput,
    InfoBankAccount,
    InfoResponse,
    InfoUpdateBankAccountInput,
    infoAddAdvisorInputSchema,
    infoBankAccountSchema,
    infoCreateBankAccountInputSchema,
    infoDeleteAdvisorInputSchema,
    infoDeleteBankAccountInputSchema,
    infoResponseSchema,
    infoUpdateAddressInputSchema,
    infoUpdateBankAccountInputSchema,
    infoUpdateRolesInputSchema,
} from '../info/infoSchemas';

export const INFO_QUERY_KEY = ['info'] as const;

const parseInfoResponse = (payload: unknown): InfoResponse => {
    if (payload === null || payload === undefined) {
        return EMPTY_INFO_RESPONSE;
    }

    if (typeof payload === 'object' && payload !== null && Object.keys(payload as Record<string, unknown>).length === 0) {
        return EMPTY_INFO_RESPONSE;
    }

    const parsed = infoResponseSchema.safeParse(payload);
    if (parsed.success) {
        return parsed.data;
    }

    const source = payload as Record<string, unknown>;
    const normalizedPayload = {
        roles: source.roles ?? undefined,
        bank_accounts: source.bank_accounts ?? source.bankAccounts ?? [],
        address:
            source.address
            ?? (typeof source.address_text === 'string' ? { address_text: source.address_text } : undefined),
    };
    const normalized = infoResponseSchema.safeParse(normalizedPayload);
    if (normalized.success) {
        return normalized.data;
    }

    return EMPTY_INFO_RESPONSE;
};

export const useInfo = () => {
    const { data, isLoading, isError, error, refetch } = useQuery<InfoResponse, Error>({
        queryKey: INFO_QUERY_KEY,
        queryFn: async () => {
            try {
                const response = await customFetcherRaw({ endpoint: '/info/' });
                const contentType = response.headers.get('content-type') ?? '';

                if (!contentType.toLowerCase().includes('application/json')) {
                    const fallbackText = await response.text();
                    if (!fallbackText.trim()) {
                        return EMPTY_INFO_RESPONSE;
                    }
                    try {
                        const parsedText = JSON.parse(fallbackText) as unknown;
                        return parseInfoResponse(parsedText);
                    } catch {
                        return EMPTY_INFO_RESPONSE;
                    }
                }

                const payload = (await response.json()) as unknown;
                return parseInfoResponse(payload);
            } catch (error) {
                if (error instanceof Error && /\b404\b/.test(error.message)) {
                    return EMPTY_INFO_RESPONSE;
                }
                throw error;
            }
        },
        refetchOnWindowFocus: false,
    });

    return {
        info: data,
        isLoadingInfo: isLoading,
        isErrorInfo: isError,
        infoError: error,
        refetchInfo: refetch,
    };
};

const invalidateInfo = async (queryClient: ReturnType<typeof useQueryClient>): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: INFO_QUERY_KEY });
};

export const useUpdateRoles = () => {
    const queryClient = useQueryClient();

    return useMutation<InfoResponse, Error, { roles: Partial<Record<InfoRoleKey, number | null>> }>({
        mutationFn: async (input) => {
            const parsed = infoUpdateRolesInputSchema.parse({
                roles: input.roles,
            });
            const payload: Record<string, number | null> = {};
            if (Object.prototype.hasOwnProperty.call(parsed.roles, 'ah_chair')) {
                payload.ah_chair_user_id = parsed.roles.ah_chair ?? null;
            }
            if (Object.prototype.hasOwnProperty.call(parsed.roles, 'ah_vice')) {
                payload.ah_vice_user_id = parsed.roles.ah_vice ?? null;
            }
            if (Object.prototype.hasOwnProperty.call(parsed.roles, 'treasurer')) {
                payload.treasurer_user_id = parsed.roles.treasurer ?? null;
            }
            if (Object.prototype.hasOwnProperty.call(parsed.roles, 'liaison')) {
                payload.liaison_user_id = parsed.roles.liaison ?? null;
            }
            if (Object.prototype.hasOwnProperty.call(parsed.roles, 'hut_manager')) {
                payload.hut_manager_user_id = parsed.roles.hut_manager ?? null;
            }
            if (Object.prototype.hasOwnProperty.call(parsed.roles, 'X')) {
                payload.x_user_id = parsed.roles.X ?? null;
            }
            if (Object.prototype.hasOwnProperty.call(parsed.roles, 'XX')) {
                payload.xx_user_id = parsed.roles.XX ?? null;
            }
            if (Object.prototype.hasOwnProperty.call(parsed.roles, 'XXX')) {
                payload.xxx_user_id = parsed.roles.XXX ?? null;
            }
            const response = await customFetcher<unknown>({
                endpoint: '/info/roles/',
                method: 'PATCH',
                body: payload,
            });
            return infoResponseSchema.parse({
                roles: response,
                bank_accounts: [],
                address: null,
            });
        },
        onSuccess: async () => {
            await invalidateInfo(queryClient);
        },
    });
};

export const useAddAdvisor = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { user_id: number }>({
        mutationFn: async (input) => {
            const parsed = infoAddAdvisorInputSchema.parse(input);
            await customFetcher<unknown>({
                endpoint: '/info/advisors/',
                method: 'POST',
                body: parsed,
            });
        },
        onSuccess: async () => {
            await invalidateInfo(queryClient);
        },
    });
};

export const useDeleteAdvisor = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { advisor_id: number }>({
        mutationFn: async (input) => {
            const parsed = infoDeleteAdvisorInputSchema.parse(input);
            await customFetcher<void>({
                endpoint: `/info/advisors/${parsed.advisor_id}/`,
                method: 'DELETE',
            });
        },
        onSuccess: async () => {
            await invalidateInfo(queryClient);
        },
    });
};

export const useCreateBankAccount = () => {
    const queryClient = useQueryClient();

    return useMutation<InfoBankAccount, Error, InfoCreateBankAccountInput>({
        mutationFn: async (input) => {
            const parsed = infoCreateBankAccountInputSchema.parse(input);
            const response = await customFetcher<unknown>({
                endpoint: '/info/bank-accounts/',
                method: 'POST',
                body: parsed,
            });
            return infoBankAccountSchema.parse(response);
        },
        onSuccess: async () => {
            await invalidateInfo(queryClient);
        },
    });
};

export const useUpdateBankAccount = () => {
    const queryClient = useQueryClient();

    return useMutation<InfoBankAccount, Error, InfoUpdateBankAccountInput>({
        mutationFn: async (input) => {
            const parsed = infoUpdateBankAccountInputSchema.parse(input);
            const response = await customFetcher<unknown>({
                endpoint: `/info/bank-accounts/${parsed.id}/`,
                method: 'PATCH',
                body: {
                    display_name: parsed.display_name,
                    account_holder: parsed.account_holder,
                    iban: parsed.iban,
                    bic: parsed.bic,
                    notes: parsed.notes,
                },
            });
            return infoBankAccountSchema.parse(response);
        },
        onSuccess: async () => {
            await invalidateInfo(queryClient);
        },
    });
};

export const useDeleteBankAccount = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { id: number }>({
        mutationFn: async (input) => {
            const parsed = infoDeleteBankAccountInputSchema.parse(input);
            await customFetcher<void>({
                endpoint: `/info/bank-accounts/${parsed.id}/`,
                method: 'DELETE',
            });
        },
        onSuccess: async () => {
            await invalidateInfo(queryClient);
        },
    });
};

export const useUpdateAddress = () => {
    const queryClient = useQueryClient();

    return useMutation<{ address_text: string }, Error, { address: string }>({
        mutationFn: async (input) => {
            const parsed = infoUpdateAddressInputSchema.parse({ address_text: input.address });
            const response = await customFetcher<unknown>({
                endpoint: '/info/address/',
                method: 'PATCH',
                body: parsed,
            });
            return infoUpdateAddressInputSchema.parse(response);
        },
        onSuccess: async () => {
            await invalidateInfo(queryClient);
        },
    });
};
